// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppEvent } from '@interfaces/interfaces';
import { User } from '@sentry/angular-ivy';
import { takeWhile } from 'rxjs/operators';
import { UserDealers, UserTypes } from '../../interfaces/user.interfaces';
import { DealershipService } from '../../services/dealership.service';
import { UserService } from '../../services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { ChangeDealershipModalComponent } from 'src/app/modals/change-dealership-modal/change-dealership-modal.component';
import { SharedService } from '@services/shared.service';
import { ModalSize } from '@interfaces/shared.interfaces';
import { PreloadService } from '@services/preload.service';
import { ProductIds } from '@interfaces/document.interfaces';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-dealer',
    templateUrl: './dealer.component.html',
    styleUrls: ['./dealer.component.scss'],
})
export class DealerComponent implements OnInit, OnDestroy, OnChanges {
    @Input() userDealers: UserDealers[] = [];
    @Input() dealerSelection: UserDealers;
    @Input() user: User;
    @Input() mobileMenu?: boolean;
    @Output() public outEvent: EventEmitter<AppEvent> = new EventEmitter();

    private isComponentAlive = true;

    isLoading = false;
    isCustomer = true;
    currentDealershipName;
    dealershipLogo;
    dealershipId = this.dealershipService.id;
    pddjHidden: string;
    pddjLogo: string;
    homeLink = ['/dashboard', 'deals'];
    multipleDealerships = false;
    dealershipsForUser: UserDealers[] = [];
    maxDealershipNameLength = 25;
    activeEzDocsBaseDealers: UserDealers[] = [];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly dealershipService: DealershipService,
        private readonly userService: UserService,
        private readonly dialog: MatDialog,
        private readonly sharedService: SharedService,
        private preloadService: PreloadService
    ) {}

    async ngOnInit(): Promise<void> {
        this.route.data.pipe(takeWhile(() => this.isComponentAlive)).subscribe(() => {
            if (this.route.snapshot.data?.dealer) {
                this.dealerSelection = this.route.snapshot.data?.dealer;
                this.dealershipsForUser = [];
            }
        });

        await this.getActiveDealers();
        this.enableChangeDealershipIfUserBelongsToMultiple();
        this.checkIfUserJustLoggedIn();

        if (this.userService.user.userType !== UserTypes.Customer) {
            this.isCustomer = false;
        }
    }

    async getActiveDealers() {
        if (this.userService.user.userType !== UserTypes.Internal) {
            try {
                this.activeEzDocsBaseDealers = await firstValueFrom(this.dealershipService.getUserActiveDealers$(ProductIds.EzDocsBase));
            } catch (e) {
                console.error('Could Not Get Active Dealers:', e);
            }
        }
    }

    checkIfUserJustLoggedIn() {
        this.userService
            .getUserByEmailForLogin$(this.userService.user.email)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user) => {
                if (user.userType === UserTypes.Dealer && user.isDealershipTerminated) {
                    if (user.hasOtherDealershipsAvailable) {
                        this.changeDealershipClicked(true);
                    }
                }
            });
    }

    async enableChangeDealershipIfUserBelongsToMultiple(): Promise<void> {
        try {
            this.preloadService
                .getDealerships()
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((dealerships) => {
                    if (this.userService.user.userType !== UserTypes.Internal) {
                        const filteredActiveDealers = dealerships.filter((dealer) =>
                            this.activeEzDocsBaseDealers.some((activeDealer) => activeDealer.dealerId === dealer.dealerId)
                        );
                        this.dealershipsForUser = filteredActiveDealers;
                        this.multipleDealerships = (dealerships || []).length > 1;
                    } else {
                        this.dealershipsForUser = dealerships;
                        this.multipleDealerships = (dealerships || []).length > 1;
                    }
                });
        } catch (error) {
            console.error('An error occurred retrieving dealerships:', error);
            this.isLoading = false;
        }
    }

    trimDealerName(dealerName: string): string {
        return dealerName.length > this.maxDealershipNameLength + 3 ? dealerName.slice(0, this.maxDealershipNameLength) + '...' : dealerName;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.dealerSelection?.currentValue) {
            this.dealerSelection = changes.dealerSelection.currentValue;
        }
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    changeDealershipClicked(disableClose = false): void {
        this.dialog.open(ChangeDealershipModalComponent, {
            width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
            height: this.sharedService.getModalHeight(ModalSize.Medium) + 'px',
            closeOnNavigation: true,
            disableClose,
            data: {
                disableClose,
                user: this.userService.user,
                dealerSelection: this.dealerSelection,
                eventHandler: this.eventHandler.bind(this),
                allDealershipsForUser: this.dealershipsForUser,
            },
        });
    }

    public eventHandler(event: AppEvent): void {
        this.isLoading = true;

        switch (event.context) {
            case 'dealer-changed':
                this.dealerSelection = event.data.updatedDealer;
                break;
            case 'modal-opened':
                this.isLoading = false;
                break;
            default:
                break;
        }
    }
}
